import React, { useMemo } from 'react'
import CmdKGroup from '../CmdKGroup'
import CommandItem from '../CmdKItem'
import { useQuery } from '@apollo/client'
import { AIRCRAFTS_QUERY } from '../queries'
import { useOrgName } from 'src/hooks/useOrgName'
import { navigate, routes, useRouteName } from '@cedarjs/router'
import FlightRoundedIcon from '@mui/icons-material/FlightRounded'
import ClearAllRounded from '@mui/icons-material/ClearAllRounded'
import { useDispatch } from 'src/hooks/useDispatch'
import { resetHistoryCount } from 'src/slices/historyCountSlice'
import { filter } from '../filter'

interface CommandMenuGroup {
  heading: string
  items: {
    label: string
    icon?: React.ReactNode
    subtext?: string
    disabled?: boolean
    onClick?: () => void
    children?: {
      label: string
      icon?: React.ReactNode
      subtext?: string
      onClick?: () => void
    }[]
  }[]
}

interface DueListModuleProps {
  search: string
  onNavigate: (groups: CommandMenuGroup[]) => void
  onBack: () => void
  pages: CommandMenuGroup[][]
  onClose: () => void
}

const DueListModule: React.FC<DueListModuleProps> = ({
  onNavigate,
  pages,
  search,
  onClose,
}) => {
  const dispatch = useDispatch()
  const route = useRouteName()
  const orgName = useOrgName()
  const { data, loading } = useQuery(AIRCRAFTS_QUERY, {
    variables: { orgSlug: orgName },
  })

  if (route === 'dueList') {
    // if we are on the due list page, we don't need to show the due list module
    // we have a more useful module for that
    return null
  }

  const resetHistory = () => {
    // reset the history count so that the filters are not hydrated from the session on due list page
    dispatch(resetHistoryCount())
  }

  const handleDueListClick = () => {
    onNavigate([
      {
        heading: 'Due List',
        items: dueListItems,
      },
    ])
  }

  const dueListItems = useMemo(() => {
    const items = [
      {
        label: 'Fleet Due List',
        subtext: 'Due List',
        keywords: ['fleet', 'due', 'list'],
        startIcon: <ClearAllRounded />,
        onClick: () => {
          onClose()
          resetHistory()
          navigate(routes.dueList({ orgName }))
        },
      },
    ]

    if (data?.aircrafts) {
      items.push(
        ...data.aircrafts.map((aircraft) => ({
          label: aircraft.tailNumber,
          subtext: 'Due List',
          keywords: [aircraft.tailNumber, 'due', 'list'],
          startIcon: <FlightRoundedIcon />,
          onClick: () => {
            onClose()
            resetHistory()
            navigate(routes.dueList({ aircrafts: aircraft.id, orgName }))
          },
        }))
      )
    }

    return items
  }, [data?.aircrafts, orgName, onClose, resetHistory])

  // Only show the top-level item when we're at root level
  if (pages.length > 0) {
    return null
  }

  const aircraftCount = data?.aircrafts?.length || 0

  const filteredDueListItems = dueListItems.filter((item) =>
    filter(item.label, search, item.keywords)
  )

  if (filteredDueListItems.length === 0) {
    return null
  }

  // If there's a search term, show items (or loading state)
  if (search) {
    if (data?.aircrafts) {
      return (
        <CmdKGroup heading="Due List">
          {filteredDueListItems.map((item, index) => {
            return (
              <CommandItem
                key={index}
                onSelect={item.onClick}
                startIcon={item.startIcon}
                subtext={item.subtext}
                keywords={item.keywords}
              >
                {item.label}
              </CommandItem>
            )
          })}
        </CmdKGroup>
      )
    }
  }

  // Show parent item if no search term
  return (
    <CmdKGroup heading="Due List">
      <CommandItem
        onSelect={handleDueListClick}
        startIcon={<ClearAllRounded />}
        subtext={`${aircraftCount} aircraft available`}
      >
        Due List
      </CommandItem>
    </CmdKGroup>
  )
}

export default DueListModule
